import {
  ENUM_LABELS,
  type RequisitionDetail,
  type RequisitionStatus,
  formatPersonName,
} from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { DetailList } from '#client/components/ui/DetailList';
import { TextField } from '#client/components/ui/TextField';
import { formatDate, formatDateTime, formatMoney, dash } from '#client/lib/format';
import { routes } from '#client/lib/routes';
import { TONES, type TransitionAction } from './requisition-actions';
import { calculateOrderValue } from './requisition.logic';
import { ButtonLink } from '#client/components/ui/ButtonLink';

interface RequisitionActionsProps {
  requisitionId: number;
  actions: readonly TransitionAction[];
  pendingTo: RequisitionStatus | null;
  activeTo: RequisitionStatus | null;
  canRecordDelivery: boolean;
  onMove: (to: RequisitionStatus) => void;
}

export function RequisitionActions({
  requisitionId,
  actions,
  pendingTo,
  activeTo,
  canRecordDelivery,
  onMove,
}: RequisitionActionsProps) {
  return (
    <div className="c-card__actions">
      {actions.map((action) => (
        <Button
          key={action.to}
          variant={action.variant}
          size="sm"
          loading={pendingTo === action.to}
          disabled={activeTo !== null && activeTo !== action.to}
          onClick={() => onMove(action.to)}
        >
          {action.label}
        </Button>
      ))}
      {canRecordDelivery && (
        <ButtonLink variant="primary" size="sm" to={routes.requisitionReceive(requisitionId)}>
          Record a delivery
        </ButtonLink>
      )}
    </div>
  );
}

interface RejectFormProps {
  reason: string;
  onReasonChange: (reason: string) => void;
  onReject: () => void;
}

export function RejectForm({ reason, onReasonChange, onReject }: RejectFormProps) {
  return (
    <div className="c-requisition__reason">
      <TextField
        label="Why is it being rejected?"
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
        helpText="Required. An unexplained refusal is what the bench asks about a week later and nobody can answer."
        required
      />
      <Button variant="danger" onClick={onReject} disabled={!reason.trim()}>
        Reject this order
      </Button>
    </div>
  );
}

export function RequisitionDetails({ requisition }: { requisition: RequisitionDetail }) {
  return (
    <DetailList
      items={[
        {
          label: 'Status',
          value: (
            <Badge tone={TONES[requisition.status]}>
              {ENUM_LABELS.requisitionStatus[requisition.status]}
            </Badge>
          ),
        },
        { label: 'Supplier', value: requisition.supplier?.name ?? 'Not chosen' },
        { label: 'Bench', value: dash(requisition.department?.name) },
        {
          label: 'Raised by',
          value: `${formatPersonName(requisition.requested_by)} · ${formatDateTime(requisition.created_at)}`,
        },
        ...(requisition.approved_by
          ? [
              {
                label: requisition.status === 'rejected' ? 'Rejected by' : 'Approved by',
                value: formatPersonName(requisition.approved_by),
              },
            ]
          : []),
        ...(requisition.expected_at
          ? [{ label: 'Expected', value: formatDate(requisition.expected_at) }]
          : []),
        ...(requisition.note ? [{ label: 'Note', value: requisition.note }] : []),
      ]}
    />
  );
}

export function ReagentsCard({ lines }: { lines: RequisitionDetail['lines'] }) {
  const orderValue = calculateOrderValue(lines);

  return (
    <Card title="Reagents" subtitle={`${lines.length} on this order`} flush>
      <div className="c-table__scroll" tabIndex={0}>
        <table className="c-table">
          <thead>
            <tr>
              <th scope="col">Reagent</th>
              <th scope="col">Pack</th>
              <th scope="col" className="c-table__cell--end">
                Ordered
              </th>
              <th scope="col" className="c-table__cell--end">
                Arrived
              </th>
              <th scope="col" className="c-table__cell--end">
                Price/pack
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <td>
                  {line.item.name}
                  <span className="c-inventory__code">{line.item.code}</span>
                </td>
                <td>{line.item.pack_label}</td>
                <td className="c-table__cell--end">{line.quantity_packs}</td>
                <td className="c-table__cell--end">
                  <span
                    className={
                      line.received_packs >= line.quantity_packs
                        ? 'c-requisition__settled'
                        : undefined
                    }
                  >
                    {line.received_packs}
                  </span>
                </td>
                <td className="c-table__cell--end">
                  {line.unit_price === null ? '—' : formatMoney(line.unit_price)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orderValue > 0 && (
        <dl className="c-summary">
          <div className="c-summary__row c-summary__row--total">
            <dt>Order value, at the quoted prices</dt>
            <dd>{formatMoney(orderValue)}</dd>
          </div>
        </dl>
      )}
    </Card>
  );
}

export function Deliveries({ receipts }: { receipts: RequisitionDetail['receipts'] }) {
  if (receipts.length === 0) return null;

  return (
    <Card title="Deliveries" subtitle={`${receipts.length} recorded`}>
      <ul className="c-delivery-list">
        {receipts.map((receipt) => (
          <li key={receipt.id} className="c-delivery">
            <div className="c-delivery__head">
              <strong>{formatDate(receipt.received_at)}</strong>
              {receipt.invoice_no && (
                <span className="c-muted"> · invoice {receipt.invoice_no}</span>
              )}
              <span className="c-muted">
                {' '}
                · signed for by {formatPersonName(receipt.received_by)}
              </span>
            </div>
            <ul className="c-delivery__lines">
              {receipt.lines.map((line) => (
                <li key={line.id}>
                  {line.quantity_packs} × {line.item.name} · lot {line.lot_no}
                  {line.expiry_at && ` · expires ${formatDate(line.expiry_at)}`}
                  {line.pack_price > 0 && ` · ${formatMoney(line.pack_price)}/pack`}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Card>
  );
}
